import { NavMarker } from 'react-rhumb';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ApiError from '../../../../components/Errors/ApiError';
import { useIndividualCertificationOverview } from '../../myTeamQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import UserAvatarLink from '../common/UserAvatarLink';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useCertificationProfileActiveTableStore } from '../../store/myTeamTableStateStore';
import { useParams } from 'quartz-routing/react-router';
import { parseCertificationStatusFilter } from '../../utils/filterUtils';
import EmptyTable from '../common/EmptyTable';
import MyTeamTablePagination from '../common/TablePagination';
import { useTableSorting } from '../../hooks/tableHooks';
import { trackAwardLinkClick, trackDownloadCertificateClick } from '../../utils/trackingUtils';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import ViewAwardButton from '../common/ViewAwardButton';
import { parseTrackIdFromExamLink } from 'academy-common-lib/utils/certificates';
import { useUserInfo } from '../../../accounts/accountsQueries';
import { useTheme } from 'foundations-theming/utils';
import { useRef } from 'react';
import DateTableCell from '../common/DateTableCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CertificationProfileActiveTable = ({
  certificationName
}) => {
  const theme = useTheme();
  const {
    certificationId,
    certificationStatus: certificationStatusParam
  } = useParams();
  const certificationProfileActiveTableStore = useCertificationProfileActiveTableStore();
  const {
    state: {
      search,
      teams,
      users,
      sortBy,
      sortOrder,
      page,
      pageSize
    },
    actions: {
      resetTableState,
      setSortBy,
      setSortOrder,
      setPage,
      setPageSize
    }
  } = certificationProfileActiveTableStore;
  const {
    isNotTablet
  } = useMediaQuery();
  const totalResultsRef = useRef(0);
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const {
    userInfo
  } = useUserInfo();
  const offsetPosition = getOffsetPosition(page, pageSize);
  const {
    data: teamMemberRecords,
    loading,
    error
  } = useIndividualCertificationOverview({
    certificationId: Number(certificationId),
    certificationStatus: parseCertificationStatusFilter(certificationStatusParam),
    teamIds: teams,
    userIds: users,
    nameFilter: search,
    pageLength: pageSize,
    pageSize,
    sortField: sortBy,
    sortOrder: sortOrder,
    offset: {
      offsetPosition
    }
  });
  if ((teamMemberRecords === null || teamMemberRecords === void 0 ? void 0 : teamMemberRecords.results.length) === 0) {
    return /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "CERTIFICATION_TABLE_EMPTY",
      onResetClick: resetTableState
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATION_TABLE_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  if (!loading && teamMemberRecords) {
    totalResultsRef.current = teamMemberRecords.total;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [loading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: 4,
      rows: pageSize
    }) : teamMemberRecords && /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATION_TABLE_LOADED",
      children: /*#__PURE__*/_jsxs(Table, {
        condensed: true,
        "data-test-id": "certification-profile-table",
        hover: true,
        responsive: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '25%',
              borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '25%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '25%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '25%'
            }
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('USER_NAME'),
              sort: getSortDirectionForColumn('USER_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.name"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('COMPLETED_AT'),
              sort: getSortDirectionForColumn('COMPLETED_AT'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.completed"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('EXPIRES_AT'),
              sort: getSortDirectionForColumn('EXPIRES_AT'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.expiration"
              })
            }), /*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.awardLink"
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: teamMemberRecords.results.map((teamMemberAward, index) => {
            return /*#__PURE__*/_jsxs("tr", {
              "data-test-id": "certification-profile-active-table-data-row",
              children: [/*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(UserAvatarLink, {
                  displayAvatar: isNotTablet,
                  position: getPaginatedPosition(offsetPosition, index),
                  userEmail: teamMemberAward.userEmail,
                  userId: teamMemberAward.userId,
                  userName: teamMemberAward.userName
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "table-completed-at",
                children: teamMemberAward.completedAt && /*#__PURE__*/_jsx(FormattedDateTime, {
                  format: "ll",
                  value: teamMemberAward.completedAt
                })
              }), /*#__PURE__*/_jsx(DateTableCell, {
                testId: "table-expires-at",
                value: teamMemberAward.expiresAt
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "table-award-link",
                children: teamMemberAward.grantedCredentialId && /*#__PURE__*/_jsx(ViewAwardButton, {
                  grantedCredentialId: teamMemberAward.grantedCredentialId,
                  onClick: () => {
                    trackAwardLinkClick(teamMemberAward.certificationId, certificationName);
                  },
                  onDownloadCertificateClick: () => {
                    trackDownloadCertificateClick(teamMemberAward.certificationId, certificationName);
                  },
                  showShareButton: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id) === teamMemberAward.userId,
                  trackId: parseTrackIdFromExamLink(teamMemberAward.examLink)
                })
              })]
            }, teamMemberAward.userId);
          })
        })]
      })
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
      page: page,
      pageSize: pageSize,
      totalResults: totalResultsRef.current
    })]
  });
};
export default CertificationProfileActiveTable;